import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import { AuthContext } from '../../context/AuthContext';
import apiService from '../../components/services/apiService';
import Sidebar from '../../components/layout/Sidebar';
import ProductsAdmin from './ProductsAdmin';
import OrdersAdmin from './OrdersAdmin';
import FlashSaleAdmin from './FlashSaleAdmin';
import CategoriesAdmin from './CategoriesAdmin';
import { LogOut, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatVND } from '../../utils/formatters';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { showToast } = useToast();
  const { logout } = useContext(AuthContext);

  // State lưu trữ dữ liệu thống kê tổng quan từ API
  const [stats, setStats] = useState({
    revenue: 0,
    newOrders: 0,
    totalStock: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Hàm gọi API lấy số liệu thống kê tổng quan
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      // Bạn có thể đổi đường dẫn endpoint '/admin/stats' cho khớp với Controller bên Java Spring Boot của bạn
      const response = await apiService.get('/admin/stats');
      const data = response.data !== undefined ? response.data : response;
      
      setStats({
        revenue: data.revenue || data.totalRevenue || 0,
        newOrders: data.newOrders || data.orderCount || 0,
        totalStock: data.totalStock || data.stockCount || 0
      });
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu thống kê tổng quan:", error);
      // Giữ giá trị mặc định hoặc hiển thị thông báo nhẹ nhàng nếu cần
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    }
  }, [activeTab, fetchDashboardStats]);

  const handleConfirmLogout = async () => {
    try {
      await apiService.post("/auth/logout"); 
    } catch (error) {
      console.error("Lỗi đăng xuất từ server:", error);
    } finally {
      logout();
      setShowLogoutModal(false);
      showToast("Đăng xuất thành công!", "success");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        onLogout={() => setShowLogoutModal(true)} 
      />
      
      <main className="flex-1 p-8 overflow-y-auto">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold font-serif">Tổng Quan Quản Trị</h1>
              <button 
                onClick={fetchDashboardStats}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs text-slate-300 transition cursor-pointer"
                title="Làm mới dữ liệu"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingStats ? 'animate-spin' : ''}`} />
                <span>Làm mới</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Thẻ Doanh Thu */}
              <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-lg">
                <span className="text-xs text-slate-400 font-medium">Doanh Thu</span>
                <p className="text-2xl font-bold mt-2 text-indigo-400">
                  {loadingStats ? 'Đang tải...' : formatVND(stats.revenue)}
                </p>
              </div>

              {/* Thẻ Đơn Hàng Mới */}
              <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-lg">
                <span className="text-xs text-slate-400 font-medium">Đơn Hàng Mới</span>
                <p className="text-2xl font-bold mt-2 text-emerald-400">
                  {loadingStats ? 'Đang tải...' : stats.newOrders}
                </p>
              </div>

              {/* Thẻ Sản Phẩm Tồn Kho */}
              <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-lg">
                <span className="text-xs text-slate-400 font-medium">Sản Phẩm Tồn Kho</span>
                <p className="text-2xl font-bold mt-2 text-amber-400">
                  {loadingStats ? 'Đang tải...' : stats.totalStock}
                </p>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'categories' && <CategoriesAdmin />}
        {activeTab === 'products' && <ProductsAdmin />}
        {activeTab === 'orders' && <OrdersAdmin />}
        {activeTab === 'flash-sale' && <FlashSaleAdmin />}
      </main>

      {/* --- MODAL XÁC NHẬN ĐĂNG XUẤT --- */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 space-y-6 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-500 shrink-0">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Xác nhận đăng xuất</h3>
                <p className="text-sm text-slate-400 mt-1">Bạn có chắc chắn muốn thoát khỏi phiên làm việc quản trị này không?</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-bold transition text-slate-300 cursor-pointer"
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                onClick={handleConfirmLogout}
                className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold transition flex items-center gap-2 cursor-pointer"
              >
                <LogOut size={16} /> Đăng xuất
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}